// Piezas compartidas del espacio FONDO CRIPTO (Plutus) — admin-only.
// Tipos de la API /fund/* + badges propios (cripto). Reutiliza el visor de
// pantallazos y utilidades de la bitácora de Njord (genéricos), pero con sus
// propios kinds (incluye "rebalanceo") y decisiones (comprar/vender/mantener).

import { type ReactElement } from 'react';
import { cn } from '../../lib/cn';
import {
  ScreenshotGrid,
  Lightbox,
  BIAS_TONE,
  fmtFecha,
  type JournalScreenshot,
} from '../journal/shared';

export { ScreenshotGrid, Lightbox, BIAS_TONE, fmtFecha };
export type { JournalScreenshot };

export type FundKind = 'analisis' | 'leccion' | 'hito' | 'rebalanceo';

export interface FundEntry {
  id: string;
  kind: FundKind;
  title: string;
  summary: string;
  tags: string[];
  symbol: string | null;
  bias: string | null;
  decision: string | null;
  outcome: string | null;
  tradeRef: string | null;
  author: string | null;
  source: string;
  createdAt: string;
  updatedAt: string;
  screenshots: JournalScreenshot[];
}

export interface FundRule {
  id: string;
  category: string;
  title: string;
  body: string;
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

export const FUND_KIND_META: Record<FundKind, { label: string; tone: string; dot: string }> = {
  analisis: {
    label: 'Análisis',
    tone: 'text-info-DEFAULT bg-info-DEFAULT/10 border-info-DEFAULT/30',
    dot: 'bg-info-DEFAULT',
  },
  leccion: {
    label: 'Lección',
    tone: 'text-accent-500 bg-accent-500/10 border-accent-500/30',
    dot: 'bg-accent-500',
  },
  hito: {
    label: 'Hito',
    tone: 'text-win-DEFAULT bg-win-DEFAULT/10 border-win-DEFAULT/30',
    dot: 'bg-win-DEFAULT',
  },
  rebalanceo: {
    label: 'Rebalanceo',
    tone: 'text-warn-DEFAULT bg-warn-DEFAULT/10 border-warn-DEFAULT/30',
    dot: 'bg-warn-DEFAULT',
  },
};

// Decisiones del fondo cripto (distintas del operar/observar/esperar de Njord).
export const FUND_DECISION_META: Record<string, { label: string; tone: string }> = {
  comprar: { label: '▲ Comprar', tone: 'text-win-DEFAULT border-win-DEFAULT/40' },
  vender: { label: '▼ Vender', tone: 'text-loss-DEFAULT border-loss-DEFAULT/40' },
  mantener: { label: '◆ Mantener', tone: 'text-info-DEFAULT border-info-DEFAULT/40' },
  observar: { label: '◉ Observar', tone: 'text-warn-DEFAULT border-warn-DEFAULT/40' },
};

export const RULE_CATEGORY_META: Record<string, { label: string; emoji: string }> = {
  riesgo: { label: 'Riesgo', emoji: '🛡️' },
  exposicion: { label: 'Exposición', emoji: '⚖️' },
  'co-firma': { label: 'Co-firma', emoji: '✍️' },
  estilo: { label: 'Estilo', emoji: '🎯' },
  entrada: { label: 'Entradas', emoji: '📥' },
  salida: { label: 'Salidas', emoji: '📤' },
  general: { label: 'General', emoji: '📌' },
};

export function FundKindBadge({ kind }: { kind: FundKind }): ReactElement {
  const m = FUND_KIND_META[kind] ?? FUND_KIND_META.analisis;
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full border text-[11px] font-semibold uppercase tracking-wider',
        m.tone,
      )}
    >
      <span className={cn('w-1.5 h-1.5 rounded-full', m.dot)} />
      {m.label}
    </span>
  );
}
